<template>
  <!-- 商机详情：基本信息 + 跟进动态 -->
  <div class="defaultViewStyle opportunity-detail">
    <div class="od-root">
      <!-- 头部信息卡 -->
      <div class="chart-card">
        <div class="od-header">
          <el-page-header @back="goBack" :content="(detail.opportunityNo || '') + ' ' + (detail.opportunityTitle || '')"/>
          <div class="od-tags">
            <el-tag size="small" :type="stageTagType(detail.currentStage)">{{ stageName(detail.currentStage) || '—' }}</el-tag>
            <el-tag v-if="sourceName(detail.sourceDictId)" size="small" type="info">{{ sourceName(detail.sourceDictId) }}</el-tag>
          </div>
        </div>
        <div class="od-meta">
          <span>负责人：{{ detail.ownerEmployeeNames || '—' }}</span>
          <span>预计成交：{{ detail.expectDealDate || '—' }}</span>
          <span>商机金额：<span class="num bold">{{ money(detail.opportunityAmount) }}</span></span>
          <span>成交概率：{{ detail.winRate || 0 }}%</span>
        </div>
      </div>

      <!-- 商机信息 -->
      <div class="chart-card">
        <div class="section-title">商机信息</div>
        <div class="od-info">
          <div class="od-info-item"><span class="k">商机编号</span><span class="v">{{ detail.opportunityNo || '—' }}</span></div>
          <div class="od-info-item"><span class="k">实际成交</span><span class="v">{{ detail.actualDealDate || '—' }}</span></div>
          <div class="od-info-item"><span class="k">创建时间</span><span class="v">{{ detail.createdTime || '—' }}</span></div>
          <div class="od-info-item"><span class="k">加权预测金额</span><span class="v num bold">{{ money(detail.predictAmount) }}</span></div>
          <div class="od-info-item full" v-if="detail.closeReason"><span class="k">输单原因</span><span class="v">{{ detail.closeReason }}</span></div>
          <div class="od-info-item full"><span class="k">备注</span><span class="v">{{ detail.remark || '—' }}</span></div>
        </div>
      </div>

      <!-- 企业微信关联（仅企业微信环境） -->
      <div v-if="wxVisible && detail.partnerId" class="chart-card">
        <form-items :columns="1">
          <partner-wx-bind :partner-id="detail.partnerId"/>
        </form-items>
      </div>

      <!-- 跟进动态 -->
      <div class="chart-card">
        <div class="section-title">跟进动态（{{ (detail.followList || []).length }} 条）</div>
        <yq-table :data="detail.followList || []" :empty-text="'暂无跟进记录'" height="auto">
          <yq-table-column label="跟进人" prop="followEmployeeName" width="120rem"/>
          <yq-table-column label="商机阶段" prop="stageValue" width="120rem">
            <template v-slot="{row}">{{ stageName(row.stageValue) }}</template>
          </yq-table-column>
          <yq-table-column label="跟进内容" prop="followContent" min-width="260rem" :show-overflow-tooltip="true"/>
          <!-- 两个时间都只显示到天：跟进是「哪天跟的、哪天再跟」，时分秒没有意义 -->
          <yq-table-column label="跟进时间" prop="followTime" width="180rem">
            <template v-slot="{row}">{{ dateOnly(row.followTime) }}</template>
          </yq-table-column>
          <yq-table-column label="下次跟进" prop="nextFollowDate" width="120rem">
            <template v-slot="{row}">{{ dateOnly(row.nextFollowDate) }}</template>
          </yq-table-column>
        </yq-table>
      </div>
    </div>
  </div>
</template>

<script>
import YqTable from "@/components/h5/table/YqTable.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import PartnerWxBind from "@/components/h5/partner/PartnerWxBind.vue";
import opportunityApi from "@/api/dz-crm/partner/opportunityApi";
import {DICT_OPPORTUNITY_STAGE, DICT_PARTNER_ORIGIN, dictName, stageTagType} from "@/views/h5/crm/crmDictionary";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";


export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "OpportunityDetail",
  components: {PartnerWxBind, FormItems, YqTable},
  data() {
    return {detail: {}, stageList: [], sourceList: []};
  },
  computed: {
    wxVisible() {
      return [2, 3].includes(Util.getContextPath());
    },
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    // 阶段名、来源名由字典关联 —— 后端只返回值，不返回名称
    commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE).then(list => this.stageList = list || []);
    commonApi.queryUniqueDictionaryList(DICT_PARTNER_ORIGIN).then(list => this.sourceList = list || []);
    if (this.route.query.id) {
      opportunityApi.findById(this.route.query.id).then(res => this.detail = res || {});
    }
  },
  methods: {
    goBack() {
      this.router.back();
    },
    /** 日期只取到天（后端返回可能带时间部分） */
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "";
    },
    stageName(stageValue) {
      return dictName(this.stageList, stageValue);
    },
    sourceName(sourceId) {
      return dictName(this.sourceList, sourceId);
    },
    stageTagType(stageValue) {
      return stageTagType(stageValue);
    },
    money(value) {
      if (value === null || value === undefined) {
        return "—";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
  }
}

.od-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .od-tags {
    display: flex;
    gap: 8px;
  }
}

.od-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  font-size: 13px;
  color: #606266;
  margin-top: 12px;
}

.od-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 8px 18px;

  .od-info-item {
    display: flex;
    font-size: 13px;
    padding: 6px 0;
    border-bottom: 1px dashed var(--yq-border-color, #ebeef5);

    &.full {
      grid-column: 1 / -1;
    }

    .k {
      width: 100px;
      color: #909399;
      flex-shrink: 0;
    }

    .v {
      color: #303133;
      word-break: break-all;
    }
  }
}

.num {
  font-variant-numeric: tabular-nums;
}

.bold {
  font-weight: 600;
  color: #303133;
}
</style>
